/**
 * Current user + family signals.
 *
 * Initialised from /api/auth/session on app boot. Updated imperatively
 * by the family-setup and avatar-picker flows.
 */

import { signal } from '@preact/signals';
import { api } from '../lib/api';

export interface CurrentUser {
  id: string;
  name: string;
  age: number | null;
  role: 'child' | 'parent';
  avatar: string | null;
  family_id: string;
  family_name?: string;
  locale?: 'en' | 'nl';
}

export interface FamilyMember {
  id: string;
  name: string;
  age: number | null;
  role: 'child' | 'parent';
  avatar: string | null;
}

export const currentUser = signal<CurrentUser | null>(null);
export const familyMembers = signal<FamilyMember[] | null>(null);
export const currentFamilyId = signal<string | null>(null);
export const isParentElevated = signal(false);
/** 'new' | 'family-only' (pick a child) | 'active' */
export const sessionState = signal<'loading' | 'new' | 'family-only' | 'active'>('loading');

export async function loadSession(): Promise<void> {
  try {
    const resp = await api<
      | { ok: true; state: 'new'; user: null }
      | { ok: true; state: 'family-only'; familyId: string; members: FamilyMember[] }
      | { ok: true; state: 'active'; user: CurrentUser }
    >('/api/auth/session');

    if (resp.state === 'active') {
      currentUser.value = resp.user;
      currentFamilyId.value = resp.user.family_id;
      sessionState.value = 'active';
    } else if (resp.state === 'family-only') {
      familyMembers.value = resp.members;
      currentFamilyId.value = resp.familyId;
      sessionState.value = 'family-only';
    } else {
      sessionState.value = 'new';
    }
  } catch {
    // Treat session errors as new-session so the UI never gets stuck.
    sessionState.value = 'new';
  }
}

export async function enterAsUser(userId: string): Promise<void> {
  await api('/api/auth/enter', { method: 'POST', body: { userId } });
  await loadSession();
}
